import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import useSessionInfo from '../../data/queryHooks/useSessionInfo';
import { flowPath } from '../../scripts/flowPath';
import { privateRoutes, publicRoutes, nonFlowRoutes } from '../../scripts/routes';
import { sessionTracker } from 'ash-session-tracker';
import { SessionStorageKey } from '../../scripts/miscConstants';

const FlowGuard = ({ appFlows, children }) => {
    const sessionInfo = useSessionInfo();
    const { isLoggedIn, hasProgram, ssId } = sessionInfo;

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Track user session timeout. Once the session has ended, it reroutes the user to the logout location.
        // Extend session for whitelisted routes
        const whitelist = [
            'events.data.microsoft.com',
            'api.flightproxy.skype.com',
        ];
        sessionTracker(isLoggedIn, { whitelist });
    }, [isLoggedIn]);

    useEffect(() => {
        const isPrivateRoute = privateRoutes.find(
            route => location.pathname && location.pathname.includes(route)
        );

        const isPublicRoute = publicRoutes.find(
            route => location.pathname && location.pathname.includes(route)
        );

        // non-logged-in user accessing private routes will be redirected to login page
        if (isPrivateRoute && !isLoggedIn) {
            window.location.href = '/identity/login';
        }

        // logged-in user accessing public routes will be redirected to dashboard page
        if (isPublicRoute && isLoggedIn) {
            navigate('/');
        }

        // logged-in user but doesn't belong to any program will be redirected to 500 page
        if (isLoggedIn && !hasProgram) {
            navigate('/500');
        }

        if (isLoggedIn) {
            // when user is logged in and on a page other than the non-completed flow page, and the flow page can't be skipped
            // then redirect user to that flow page
            const userFlowData = SessionStorageKey.userFlow &&
                JSON.parse(sessionStorage.getItem(SessionStorageKey.userFlow)) &&
                JSON.parse(sessionStorage.getItem(SessionStorageKey.userFlow))[ssId];
            const userFlow = userFlowData || appFlows;
            let url = '';
            let isReactPage;
            let canSkip = true;
            if (Array.isArray(userFlow) && userFlow.length > 0) {
                // find the first non-completed flow and get whether canSkip
                const firstFlow = userFlow.find(item => !item.isCompleted);
                canSkip = firstFlow ? firstFlow.canSkip : true;
                const firstFlowName = firstFlow ? firstFlow.flow : '';
                // get url and isReactPage for the first non-completed flow from flowPath file
                const firstFlowPath =
                    flowPath.filter(item => item.flow === firstFlowName) || [];

                if (firstFlowPath.length > 0) {
                    url = firstFlowPath[0].url;
                    isReactPage = firstFlowPath[0].isReactPage;
                }
            }

            const isNonFlowRoute = nonFlowRoutes.find(
                route => location.pathname && location.pathname.includes(route)
            );

            if (
                url &&
                location.pathname &&
                !location.pathname.includes(url) &&
                !isNonFlowRoute &&
                !canSkip
            ) {
                // add redirect queryParam to keep track of where user wants to go, so we can redirect user to that page after all flows are completed
                const redirectURL = `?redirect=${location.pathname}`;

                isReactPage
                    ? navigate(url + redirectURL)
                    : (window.location.href = url + redirectURL);
            }
        }
    }, [location, isLoggedIn, ssId, navigate, appFlows, hasProgram]);

    return children;
};

export default FlowGuard;

FlowGuard.propTypes = {
    appFlows: PropTypes.array.isRequired,
    setAppFlows: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
};
